import { observer } from "mobx-react";
import _ from "lodash";
import { useStores } from "../../stores/root-store";
import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from "react";
import { IShopSettings, WeekDays } from "../../stores/shopSettings/index.d";
import { ShowAlert, timeToDate } from "../../utils/functions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faClock,
  faCutlery,
  faLocationPin,
  faMapLocation,
  faMessage,
} from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import moment from "moment";

const tabs = [
  { title: "Hours Settings", id: 1 },
  { title: "Orders Settings", id: 2 },
  { title: "Shop Settings", id: 3 },
];

const ShopSettings = observer(() => {
  const { shopSettingsStore } = useStores();
  const { shopSettings, getShopSettings, updateShopSettings } =
    shopSettingsStore;
  const [settings, setSettings] = useState<IShopSettings>(shopSettings);
  const [selectedTab, setSelectedTab] = useState(1);

  useEffect(() => {
    if (!shopSettings.shopId) getShopSettings();
    else setSettings(shopSettings);
  }, [shopSettings]);

  const handleFormSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (
      settings &&
      settings.shopStartHour &&
      settings.shopEndHour &&
      settings.shopOpenHoursInterval
    ) {
      if (new Date(settings.shopStartHour) >= new Date(settings.shopEndHour))
        ShowAlert.Error("Shop end hour must be greater than start hour.");
      updateShopSettings(settings);
    } else {
      ShowAlert.Error("Please make sure all the fields are entered.");
    }
  };

  const onTextChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = evt.target;
    setSettings((prev) => ({ ...prev, [id]: value }));
  };

  const onRadioChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = evt.target;
    setSettings((prev) => ({ ...prev, [id]: checked }));
  };

  const onCheckChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = evt.target;
    let checkedDays = settings.weeklyShopClosureDays?.split(",") ?? [];
    if (checked) {
      checkedDays.push(value);
    } else {
      let removeIndex = checkedDays.indexOf(value);
      checkedDays.splice(removeIndex, 1);
    }
    const newValue = checkedDays.join(",");
    setSettings((prev) => ({ ...prev, weeklyShopClosureDays: newValue }));
  };

  const onTimeChange = (date: Date | null, prop: string) => {
    let value = date ? moment(date).format("HH:mm") : "";
    setSettings((prev) => ({ ...prev, [prop]: value }));
  };

  const weekDays = useMemo(
    () =>
      Object.entries(WeekDays)
        .filter(([label]) => isNaN(Number(label)))
        .map(([label, value]) => ({ label, value: value.toString() })),
    []
  );

  return (
    <div className="container-fluid mt-3 text-start">
      <div className="col-lg-8 mx-auto mt-3">
        <h4>Shop Settings</h4>
        <ul
          className="nav nav-tabs nav-fill mb-3"
          id="settingsTabs"
          role="tablist"
        >
          {tabs.map((t) => (
            <li className="nav-item" role="presentation">
              <a
                data-mdb-tab-init
                className={`nav-link ${
                  selectedTab === t.id ? "active bg-default text-white" : ""
                }`}
                href={`#settings-tab-${t.id}`}
                role="tab"
                aria-controls={`settings-tab-${t.id}`}
                aria-selected="true"
                onClick={() => setSelectedTab(t.id)}
              >
                {t.title}
              </a>
            </li>
          ))}
        </ul>
        <div className="tab-content" id="settings-content">
          <form className="" autoComplete="off">
            {selectedTab === 1 && (
              <div
                className="tab-pane fade show active"
                id={`settings-tab-1`}
                role="tabpanel"
                aria-labelledby="settings-tabs-1"
              >
                <div className="form-check form-switch form-switch-reverse form-switch-lg mb-3">
                  <label
                    className={`form-check-label h6${
                      !settings.isShopClosed ? " text-success" : " text-danger"
                    }`}
                    htmlFor="isShopClosed"
                  >
                    {settings.isShopClosed ? "Shop Closed" : "Shop Open"}
                  </label>

                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isShopClosed"
                    checked={settings.isShopClosed}
                    onChange={onRadioChange}
                  />
                </div>

                <div className="mb-3">
                  <label className="h6">Weekly Shop Closure</label>
                  <div className="alert alert-primary">
                    {weekDays.map((day) => (
                      <div
                        className="form-check form-check-inline"
                        key={day.value}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`day${day.value}`}
                          value={day.value}
                          checked={settings.weeklyShopClosureDays?.includes(
                            day.value
                          )}
                          onChange={onCheckChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`day${day.value}`}
                        >
                          {day.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="h6">Shop Open Hours</label>
                  <div className="input-group">
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="shopStartHour"
                    >
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      Start
                    </label>

                    <DatePicker
                      selected={timeToDate(settings.shopStartHour)}
                      onChange={(date) => onTimeChange(date, "shopStartHour")}
                      id="shopStartHour"
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={5}
                      timeCaption="Time"
                      dateFormat="HH:mm"
                      timeFormat="HH:mm"
                      className="form-control form-control-lg"
                    />

                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="shopEndHour"
                    >
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      End
                    </label>
                    <DatePicker
                      selected={timeToDate(settings.shopEndHour)}
                      onChange={(date) => onTimeChange(date, "shopEndHour")}
                      id="shopEndHour"
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={5}
                      timeCaption="Time"
                      dateFormat="HH:mm"
                      timeFormat="HH:mm"
                      className="form-control form-control-lg"
                    />
                  </div>
                  <small>* 24 hours format</small>
                </div>

                <div className="mb-4">
                  <label className="h6">Open Hours Interval (Minutes)</label>
                  <div className="input-group">
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="shopOpenHoursInterval"
                    >
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      Interval
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="shopOpenHoursInterval"
                      aria-placeholder="Hour"
                      value={settings.shopOpenHoursInterval}
                      onChange={onTextChange}
                    />
                  </div>
                </div>
              </div>
            )}
            {selectedTab === 2 && (
              <div
                className="tab-pane fade show active"
                id={`settings-tab-2`}
                role="tabpanel"
                aria-labelledby="settings-tabs-2"
              >
                <div className="form-check form-switch mb-3">
                  <label
                    className={`form-check-label h6${
                      settings.allowFutureDateOrders
                        ? " text-success"
                        : " text-danger"
                    }`}
                    htmlFor="allowFutureDateOrders"
                  >
                    {settings.allowFutureDateOrders
                      ? "Future date orders allowed"
                      : "Future date orders restricted"}
                  </label>

                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="allowFutureDateOrders"
                    checked={settings.allowFutureDateOrders}
                    onChange={onRadioChange}
                  />
                </div>

                <div className="mb-3">
                  <label className="h6">Cash Payment Method</label>
                  <div>
                    <div className="form-check form-switch form-check-inline">
                      <label
                        className={`form-check-label${
                          settings.cashPaymentDelivery
                            ? " text-success"
                            : " text-danger"
                        }`}
                        htmlFor="cashPaymentDelivery"
                      >
                        {settings.cashPaymentDelivery
                          ? "Enabled for Delivery"
                          : "Restricted for Delivery"}
                      </label>

                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="cashPaymentDelivery"
                        checked={settings.cashPaymentDelivery}
                        onChange={onRadioChange}
                      />
                    </div>
                    <div className="form-check form-switch form-check-inline">
                      <label
                        className={`form-check-label${
                          settings.cashPaymentCollection
                            ? " text-success"
                            : " text-danger"
                        }`}
                        htmlFor="cashPaymentCollection"
                      >
                        {settings.cashPaymentCollection
                          ? "Enabled for Collection"
                          : "Restricted for Collection"}
                      </label>

                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="cashPaymentCollection"
                        checked={settings.cashPaymentCollection}
                        onChange={onRadioChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="h6">Minimum Order Value (£)</label>
                  <div className="input-group">
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="minimumOrderAmount"
                    >
                      for Delivery
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="minimumOrderAmount"
                      aria-placeholder="Min Order"
                      value={settings.minimumOrderAmount}
                      onChange={onTextChange}
                    />
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="minimumCollectionOrderAmount"
                    >
                      for Collection
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="minimumCollectionOrderAmount"
                      aria-placeholder="Min Order"
                      value={settings.minimumCollectionOrderAmount}
                      onChange={onTextChange}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="h6">Default Delivery Cost</label>
                  <div className="input-group">
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="defaultDeliveryCost"
                    >
                      £
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="defaultDeliveryCost"
                      aria-placeholder="Default Delivery Cost"
                      value={settings.defaultDeliveryCost}
                      onChange={onTextChange}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="h6">Delivery Radius (Miles)</label>
                  <div className="input-group">
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="orderDeliveryRadius"
                    >
                      <FontAwesomeIcon icon={faMapLocation} />
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="orderDeliveryRadius"
                      aria-placeholder="Delivery Radius"
                      value={settings.orderDeliveryRadius}
                      onChange={onTextChange}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="h6">Estimated Time (Min)</label>
                  <div className="input-group">
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="shopStartHour"
                    >
                      for Delivery
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="estimatedTimeForDelivery"
                      aria-placeholder="Delivery Time"
                      value={settings.estimatedTimeForDelivery}
                      onChange={onTextChange}
                    />
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="shopEndHour"
                    >
                      for Collection
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="estimatedTimeForCollection"
                      aria-placeholder="Collection Time"
                      value={settings.estimatedTimeForCollection}
                      onChange={onTextChange}
                    />
                  </div>
                </div>
              </div>
            )}
            {selectedTab === 3 && (
              <div
                className="tab-pane fade show active"
                id={`settings-tab-3`}
                role="tabpanel"
                aria-labelledby="settings-tabs-3"
              >
                <div className="mb-4">
                  <label className="h6">Shop Postcode</label>
                  <div className="input-group">
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="shopPostcode"
                    >
                      <FontAwesomeIcon icon={faLocationPin} />
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="shopPostcode"
                      aria-placeholder="Shop Postcode"
                      value={settings.shopPostcode}
                      onChange={onTextChange}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="h6">Notify WhatsApp Number</label>
                  <div className="input-group">
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="notifyWhatsappNumber"
                    >
                      <FontAwesomeIcon icon={faMessage} />
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="notifyWhatsappNumber"
                      aria-placeholder="Notify WhatsApp Number"
                      value={settings.notifyWhatsappNumber}
                      onChange={onTextChange}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="h6">Shop WhatsApp Number</label>
                  <div className="input-group">
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="adminWhatsappNumber"
                    >
                      <FontAwesomeIcon icon={faMessage} />
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="adminWhatsappNumber"
                      aria-placeholder="Admin WhatsApp Number"
                      value={settings.adminWhatsappNumber}
                      onChange={onTextChange}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="h6">Available Shop Tables</label>
                  <div className="input-group">
                    <label
                      className="input-group-text bg-secondary text-white"
                      htmlFor="shopTableCount"
                    >
                      <FontAwesomeIcon icon={faCutlery} />
                    </label>
                    <input
                      className="form-control form-control-lg"
                      id="shopTableCount"
                      aria-placeholder="Available Shop Tablesr"
                      value={settings.shopTableCount}
                      onChange={onTextChange}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex mt-5">
              <button
                className="btn btn-default flex-fill"
                onClick={handleFormSubmit}
                type="button"
              >
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

export default ShopSettings;
